import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  getOrgMembers, 
  getOrgTeamMembersList,
  getOrganization,
  addEmailToAllowlist,
  removeEmailFromAllowlist,
  addUserToOrgMembers,
  addUserToOrgMembersByEmail,
  removeUserFromOrgMembers,
  promoteMemberToTeamMember,
  demoteTeamMemberToMember,
  upgradeMemberToAdmin,
  demoteAdmin,
} from '../services/authService';
import SearchableEmailDropdown from '../components/SearchableEmailDropdown';
import './Members.css';

const Members = () => {
  const { orgDomain, isOrgAdminUser, isTeamMember: isTeamMemberUser } = useAuth();
  const [members, setMembers] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [allowedEmails, setAllowedEmails] = useState([]);
  const [orgType, setOrgType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingEmail, setProcessingEmail] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!orgDomain) return;

      // Get organization data
      const org = await getOrganization(orgDomain);
      if (!org) {
        setError('Organization not found');
        return;
      }

      setOrgType(org.orgType || 'university');
      setAllowedEmails(org.allowedEmails || []);

      // Load members (students with org subscriptions)
      const membersList = await getOrgMembers(orgDomain);
      setMembers(membersList);

      // Load team members (admin access but not admins)
      const teamMembersList = await getOrgTeamMembersList(orgDomain);
      setTeamMembers(teamMembersList);
    } catch (err) {
      console.error('Error loading members:', err);
      setError('Failed to load members. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [orgDomain]);

  useEffect(() => {
    if (orgDomain && (isOrgAdminUser || isTeamMemberUser)) {
      loadData();
    }
  }, [orgDomain, isOrgAdminUser, isTeamMemberUser, loadData]);

  if (!isOrgAdminUser && !isTeamMemberUser) {
    return (
      <div className="members-page">
        <div className="page-header">
          <h1>Members</h1>
        </div>
        <div className="error-state">
          <p>You must be an admin or team member to view members.</p>
        </div>
      </div>
    );
  }

  const handleEmailSelect = async (user) => {
    try {
      setProcessingEmail(user.email);
      if (isOnCampusOrg) {
        // For on-campus orgs, add to allowlist
        await addEmailToAllowlist(user.email, orgDomain);
        alert(`Added ${user.email} to allowlist`);
      } else {
        // For university orgs, add user to members array
        await addUserToOrgMembers(user.id, orgDomain);
        alert(`Added ${user.email} to members`);
      }
      await loadData();
    } catch (err) {
      console.error('Error adding email/user:', err);
      alert(`Failed to add ${user.email}. Please try again.`);
    } finally {
      setProcessingEmail(null);
    }
  };

  const handleAddNewEmail = async (email) => {
    try {
      setProcessingEmail(email);
      if (isOnCampusOrg) {
        // For on-campus orgs, add to allowlist
        await addEmailToAllowlist(email, orgDomain);
        alert(`Added ${email} to allowlist`);
      } else {
        await addUserToOrgMembersByEmail(email, orgDomain);
        alert(`Added ${email} to members`);
      }
      await loadData();
    } catch (err) {
      console.error('Error adding email/user:', err);
      alert(`Failed to add ${email}. Please try again.`);
    } finally {
      setProcessingEmail(null);
    }
  };

  const handleRemoveEmail = async (email) => {
    if (!window.confirm(`Remove ${email} from allowlist?`)) {
      return;
    }

    try {
      setProcessingEmail(email);
      await removeEmailFromAllowlist(email, orgDomain);
      await loadData();
    } catch (err) {
      console.error('Error removing email from allowlist:', err);
      alert('Failed to remove email from allowlist. Please try again.');
    } finally {
      setProcessingEmail(null);
    }
  };

  const handleRemoveMember = async (userId, email, isAdmin) => {
    if (isAdmin) {
      alert('Cannot remove an admin from members. Admins with org subscriptions will always appear in the members list.');
      return;
    }

    if (!window.confirm(`Remove ${email} from members?`)) {
      return;
    }

    try {
      setProcessingEmail(email);
      await removeUserFromOrgMembers(userId, orgDomain);
      await loadData();
    } catch (err) {
      console.error('Error removing member:', err);
      alert('Failed to remove member. Please try again.');
    } finally {
      setProcessingEmail(null);
    }
  };

  const handlePromoteToTeamMember = async (userId, email) => {
    if (!window.confirm(`Promote ${email} to team member? They will be able to access the admin panel.`)) {
      return;
    }

    try {
      setProcessingEmail(email);
      await promoteMemberToTeamMember(userId, orgDomain);
      alert(`${email} has been promoted to team member.`);
      await loadData();
    } catch (err) {
      console.error('Error promoting to team member:', err);
      alert('Failed to promote member. Please try again.');
    } finally {
      setProcessingEmail(null);
    }
  };

  const handleDemoteFromTeamMember = async (userId, email) => {
    if (!window.confirm(`Demote ${email} from team member? They will lose admin panel access.`)) {
      return;
    }

    try {
      setProcessingEmail(email);
      await demoteTeamMemberToMember(userId, orgDomain);
      alert(`${email} has been demoted to regular member.`);
      await loadData();
    } catch (err) {
      console.error('Error demoting team member:', err);
      alert('Failed to demote team member. Please try again.');
    } finally {
      setProcessingEmail(null);
    }
  };

  const handlePromoteToAdmin = async (userId, email) => {
    if (!window.confirm(`Promote ${email} to admin? They will have full admin privileges including billing access.`)) {
      return;
    }

    try {
      setProcessingEmail(email);
      await upgradeMemberToAdmin(userId, orgDomain);
      alert(`${email} has been promoted to admin.`);
      await loadData();
    } catch (err) {
      console.error('Error promoting to admin:', err);
      alert('Failed to promote member. Please try again.');
    } finally {
      setProcessingEmail(null);
    }
  };

  const handleDemoteFromAdmin = async (userId, email) => {
    if (!window.confirm(`Demote ${email} from admin? They will lose admin privileges. Keep as team member?`)) {
      return;
    }

    const keepAsTeamMember = window.confirm('Keep them as team member? (Yes = team member, No = regular member)');

    try {
      setProcessingEmail(email);
      await demoteAdmin(userId, orgDomain, keepAsTeamMember);
      alert(`${email} has been demoted ${keepAsTeamMember ? 'to team member' : 'to regular member'}.`);
      await loadData();
    } catch (err) {
      console.error('Error demoting admin:', err);
      alert('Failed to demote admin. Please try again.');
    } finally {
      setProcessingEmail(null);
    }
  };

  if (loading) {
    return (
      <div className="members-page">
        <div className="page-header">
          <h1>Members</h1>
        </div>
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading members...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="members-page">
        <div className="page-header">
          <h1>Members</h1>
        </div>
        <div className="error-state">
          <p>{error}</p>
          <button onClick={loadData} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const isOnCampusOrg = orgType === 'on_campus_org';

  return (
    <div className="members-page">
      <div className="page-header">
        <h1>Members</h1>
        {orgDomain && <p className="org-domain">Domain: {orgDomain}</p>}
        <button onClick={loadData} className="refresh-button">
          🔄 Refresh
        </button>
      </div>

      {/* Allowlist Management for On-Campus Orgs */}
      {isOnCampusOrg && (
        <div className="section-card">
          <div className="section-header">
            <h2>Email Allowlist</h2>
            <p className="section-description">
              Add emails to the allowlist. Students with these emails will automatically get an org subscription when they sign up.
            </p>
          </div>
          <div className="allowlist-management">
            <SearchableEmailDropdown
              organizationId={orgDomain}
              onSelect={handleEmailSelect}
              onAddNew={handleAddNewEmail}
              existingEmails={allowedEmails}
              placeholder="Search for student email or add new..."
            />
            {allowedEmails.length > 0 && (
              <div className="allowlist-list">
                <h3>Allowed Emails ({allowedEmails.length})</h3>
                <div className="email-list">
                  {allowedEmails.map((email) => (
                    <div key={email} className="email-item">
                      <span className="email-text">{email}</span>
                      <button
                        className="remove-email-button"
                        onClick={() => handleRemoveEmail(email)}
                        disabled={processingEmail === email}
                      >
                        {processingEmail === email ? 'Removing...' : 'Remove'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Members Section (Students with org subscriptions) */}
      <div className="section-card">
        <div className="section-header">
          <h2>Members ({members.length})</h2>
          <p className="section-description">
            {isOnCampusOrg 
              ? 'Students who have signed up and are in your allowlist. Only students in the allowlist will appear here. Admins and team members with org subscriptions will also appear here.'
              : 'Students who have signed up for SyllySync mobile app and have an org subscription. You can manually add members below. Admins and team members with org subscriptions will also appear here.'}
          </p>
        </div>
        {!isOnCampusOrg && (
          <div className="members-management">
            <SearchableEmailDropdown
              organizationId={orgDomain}
              onSelect={handleEmailSelect}
              onAddNew={handleAddNewEmail}
              existingEmails={members.map(m => m.email)}
              placeholder="Search for student email or add new to members..."
            />
          </div>
        )}
        {members.length === 0 ? (
          <div className="empty-state">
            <p>No members yet. {isOnCampusOrg ? 'Add emails to the allowlist above, and students will appear here once they sign up.' : 'Students will appear here once they sign up with an email from your domain, or you can add them manually above.'}</p>
          </div>
        ) : (
          <div className="members-list">
            {members.map((member) => (
              <div key={member.userId} className="member-card">
                <div className="member-info-row">
                  <div className="member-avatar">
                    {member.photoURL && (
                      <img 
                        src={member.photoURL} 
                        alt={member.name}
                        className="avatar-image"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const fallback = e.target.nextElementSibling;
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                    )}
                    <span className="avatar-fallback" style={{ display: member.photoURL ? 'none' : 'flex' }}>
                      {member.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="member-details">
                    <div className="member-name">
                      {member.name}
                      {member.isAdmin && (
                        <span className="admin-badge" style={{ 
                          marginLeft: '8px', 
                          padding: '2px 8px', 
                          background: '#2ab3c0', 
                          color: 'white', 
                          borderRadius: '4px', 
                          fontSize: '11px',
                          fontWeight: '600'
                        }}>
                          Admin
                        </span>
                      )}
                      {member.isTeamMember && !member.isAdmin && (
                        <span className="team-member-badge" style={{ 
                          marginLeft: '8px', 
                          padding: '2px 8px', 
                          background: '#6b7280', 
                          color: 'white', 
                          borderRadius: '4px', 
                          fontSize: '11px',
                          fontWeight: '600'
                        }}>
                          Team Member
                        </span>
                      )}
                    </div>
                    <div className="member-email">{member.email}</div>
                  </div>
                </div>
                <div className="member-actions">
                  {member.isAdmin ? (
                    <button
                      className="demote-button"
                      onClick={() => handleDemoteFromAdmin(member.userId, member.email)}
                      disabled={processingEmail === member.email}
                      style={{ padding: '6px 12px', fontSize: '12px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      {processingEmail === member.email ? 'Processing...' : 'Demote Admin'}
                    </button>
                  ) : member.isTeamMember ? (
                    <>
                      <button
                        className="promote-button"
                        onClick={() => handlePromoteToAdmin(member.userId, member.email)}
                        disabled={processingEmail === member.email}
                        style={{ padding: '6px 12px', fontSize: '12px', background: '#2ab3c0', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                      >
                        {processingEmail === member.email ? 'Processing...' : 'Promote to Admin'}
                      </button>
                      <button
                        className="demote-button"
                        onClick={() => handleDemoteFromTeamMember(member.userId, member.email)}
                        disabled={processingEmail === member.email}
                        style={{ padding: '6px 12px', fontSize: '12px', background: '#6b7280', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                      >
                        {processingEmail === member.email ? 'Processing...' : 'Demote to Member'}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="promote-button"
                        onClick={() => handlePromoteToTeamMember(member.userId, member.email)}
                        disabled={processingEmail === member.email}
                        style={{ padding: '6px 12px', fontSize: '12px', background: '#2ab3c0', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                      >
                        {processingEmail === member.email ? 'Processing...' : 'Promote to Team Member'}
                      </button>
                      <button
                        className="remove-member-button"
                        onClick={() => handleRemoveMember(member.userId, member.email, member.isAdmin)}
                        disabled={processingEmail === member.email}
                        style={{ padding: '6px 12px', fontSize: '12px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                      >
                        {processingEmail === member.email ? 'Processing...' : 'Remove'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Team Members Section (Admin access but not admins) */}
      <div className="section-card">
        <div className="section-header">
          <h2>Team Members ({teamMembers.length})</h2>
          <p className="section-description">
            People who can access the admin panel but are not admins (cannot access billing).
          </p>
        </div>
        {teamMembers.length === 0 ? (
          <div className="empty-state">
            <p>No team members yet. Team members are people who have been approved to access the admin panel but don't have admin privileges.</p>
          </div>
        ) : (
          <div className="members-list">
            {teamMembers.map((member) => (
              <div key={member.userId} className="member-card">
                <div className="member-avatar">
                  {member.photoURL && (
                    <img 
                      src={member.photoURL} 
                      alt={member.name}
                      className="avatar-image"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const fallback = e.target.nextElementSibling;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                  )}
                  <span className="avatar-fallback" style={{ display: member.photoURL ? 'none' : 'flex' }}>
                    {member.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="member-details">
                  <div className="member-name">{member.name}</div>
                  <div className="member-email">{member.email}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Members;
